import { createSlice, PayloadAction } from "@reduxjs/toolkit";

const STORAGE_KEY = "private_pricing_info";

type PrivatePricingInfo = {
  programID: string | number;
  pricing_method_id?: string | number | null;
  price?: number;
};

function parsePrivatePricing(raw: string): PrivatePricingInfo[] {
  const parsed = JSON.parse(raw);
  return Array.isArray(parsed) ? parsed : [];
}

function readPrivatePricing(): PrivatePricingInfo[] {
  try {
    const local = localStorage.getItem(STORAGE_KEY);
    if (local) return parsePrivatePricing(local);
    const session = sessionStorage.getItem(STORAGE_KEY);
    if (session) {
      localStorage.setItem(STORAGE_KEY, session);
      return parsePrivatePricing(session);
    }
  } catch (error) {
    return [];
  }
  return [];
}

function writePrivatePricing(data: PrivatePricingInfo[]) {
  const serialized = JSON.stringify(data);
  localStorage.setItem(STORAGE_KEY, serialized);
  sessionStorage.setItem(STORAGE_KEY, serialized);
}

export function getStoredCustomPricingId(
  programID: string | number | null | undefined
) {
  if (!programID) return null;
  const params = new URLSearchParams(window.location.search);
  const fromQuery =
    params.get("custom_pricing_id") || params.get("pricing_method");
  if (fromQuery) return fromQuery;
  const stored = readPrivatePricing();
  const found = stored.find(
    (item) => item.programID?.toString() === programID.toString()
  );
  return found?.pricing_method_id || null;
}

const initialState = {
  privatePricingInfo: readPrivatePricing(),
};

const programDetailsSlice = createSlice({
  name: "program-details",
  initialState,
  reducers: {
    setPrivatePricingInfo: (
      state,
      action: PayloadAction<PrivatePricingInfo>
    ) => {
      const next = action.payload;
      const filtered = state.privatePricingInfo.filter(
        (item) => item.programID?.toString() !== next.programID?.toString()
      );
      const newData = [...filtered, next];
      state.privatePricingInfo = newData;
      writePrivatePricing(newData);
    },
    removePrivatePricingInfo: (
      state,
      action: PayloadAction<string | number>
    ) => {
      const newData = state.privatePricingInfo.filter(
        (p) => p.programID.toString() !== action.payload.toString()
      );
      state.privatePricingInfo = newData;
      writePrivatePricing(newData);
    },

    removeAllPrivatePricing: (state) => {
      state.privatePricingInfo = [];
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    },
  },
});

export const {
  removePrivatePricingInfo,
  setPrivatePricingInfo,
  removeAllPrivatePricing,
} = programDetailsSlice.actions;

export default programDetailsSlice.reducer;
